import React from "react";
import { Link } from "react-router-dom";
import MelatoninOralSolutionImg from "../../images/melatonin_oral_solution_1.png";
import NimodipineOralSolutionImg from "../../images/nimodipine_oral_solution.png";
import AbirateroneAcetatePowderImg from "../../images/abiraterone_acetate_powder_1.jpg";
import useScrollReveal from "../../hooks/useScrollReveal";

export default function ProductOfferingsSection() {
  const [ref, isVisible] = useScrollReveal();

  const products = [
    {
      name: "Melatonin Oral Solution",
      strength: "2 mg/ml - 3 mg/ml",
      description: "A stable and safe liquid formulation designed for precise and customizable dosing profiles.",
      category: "Differentiated Formulation",
      features: [
        "High absorption and bioavailability",
        "Free of Glycol and Sugar",
        "Patient-compliant pediatric/geriatric suitability",
        "Stable formulation developed under QbD principles"
      ],
      image: MelatoninOralSolutionImg,
      badgeColor: "bg-amber-100 text-amber-800",
      imageFit: "object-contain",
      paddingClass: "p-4",
    },
    {
      name: "Nimodipine Oral Solution",
      strength: "10 mg/ml",
      description: "A “Alcohol Free” liquid oral dosage form developed to facilitate easier administration for patients with swallowing difficulties, particularly in critical care settings.",
      category: "Complex Generic",
      features: [
        "Ideal for nasogastric tube administration",
        "Eliminates the need for crushing tablets",
        "Reduces administration errors in hospital environments",
        "High bioequivalence mapping to innovator standards"
      ],
      image: NimodipineOralSolutionImg,
      badgeColor: "bg-blue-100 text-blue-800",
    },
    {
      name: "Abiraterone Acetate Powder for Oral Solution",
      strength: "Reconstitution Formulation",
      description: "A novel powder-for-suspension delivery system engineered to optimize dosing flexibility, patient convenience, and product stability.",
      category: "Innovative Formulation",
      features: [
        "Enhanced bioavailability and stability, with ease in swallowing",
        "User-friendly reconstitution process",
        "Cost-effective storage and logistics",
        "Improved patient compliance in oncology therapeutic regimens"
      ],
      image: AbirateroneAcetatePowderImg,
      badgeColor: "bg-teal-100 text-teal-800",
      imageFit: "object-contain",
      bgClass: "bg-[#0a9bbd]",
    }
  ];

  return (
    <section
      ref={ref}
      id="products"
      className={`py-24 bg-[#F7F9FA] relative transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-brand-teal font-bold text-xs uppercase tracking-widest block">
            Portfolio
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-brand-blue tracking-tight">
            New Product Offerings
          </h2>
          <div className="h-1 w-20 bg-brand-teal mx-auto rounded-full" />
          <p className="text-slate-500 text-sm md:text-base font-light leading-relaxed">
            Highlighting our recently developed drug formulations and delivery systems, currently available for licensing and collaborative commercialization.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {products.map((product, index) => (
            <div
              key={product.name}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-200/60 hover:border-brand-teal/20 transition-all duration-300 transform hover:-translate-y-1 flex flex-col"
            >
              {/* Product Image Area */}
              <div className={`relative h-64 w-full ${product.bgClass || "bg-white"} overflow-hidden border-b ${product.paddingClass || ""}`}>
                <img
                  src={product.image}
                  alt={product.name}
                  className={`w-full h-full ${product.imageFit || "object-cover"} transition-transform duration-700 group-hover:scale-105`}
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1507668077129-56e32842fceb?q=80&w=600&auto=format&fit=crop";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent pointer-events-none" />

                {/* Category Badge */}
                <span className={`absolute top-4 left-4 text-xs font-bold px-3 py-1 rounded-full shadow-sm ${product.badgeColor}`}>
                  {product.category}
                </span>

                {/* Strength Overlay */}
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-xs uppercase tracking-wider font-semibold opacity-85">Dosage / Strength</p>
                  <p className="text-sm font-bold">{product.strength}</p>
                </div>
              </div>

              {/* Product Info Area */}
              <div className="p-6 md:p-8 flex-grow flex flex-col justify-between space-y-6">
                <div className="space-y-4">
                  <h3 className="text-xl md:text-2xl font-bold text-brand-blue group-hover:text-brand-teal transition-colors duration-200">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed font-light">
                    {product.description}
                  </p>

                  <div className="pt-2 border-t border-slate-100">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Key Features</p>
                    <ul className="space-y-2">
                      {product.features.map((feature, i) => (
                        <li key={i} className="flex items-start text-xs md:text-sm text-slate-600 space-x-2">
                          <span className="text-brand-teal mt-0.5">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-4">
                  <Link
                    to="/ContactUs"
                    state={{ from: `Licensing Specs: ${product.name}` }}
                    className="w-full inline-flex items-center justify-center py-2.5 rounded-xl border border-brand-teal/30 hover:border-brand-teal text-brand-teal hover:bg-brand-teal/5 font-semibold text-sm transition-all duration-200"
                  >
                    Request Licensing Specs
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
